import { useMediaQuery } from "@material-ui/core";
import { Mui } from "@osu/react-ui";
import React, { forwardRef, Fragment, useContext, useEffect, useRef } from "react";
import { AppContext } from "../../App/context";
import { numOfStepsToComplete, steps as originalSteps } from "../steps";
import useStyles from "../styles";
import { CheckCircleOutline, ErrorOutline } from '@material-ui/icons';
import { createLabelId } from ".";
import { createStepHeadingTitle } from "../util/functions";
import { isActiveElementDescendantOfMain } from "../../util/functions";
import { htmlIdentifiers } from "../util/constants";

const focusOnRef = (ref, focusableId) => {
    if(ref?.current?.id && (ref?.current?.id === focusableId)) {
      const hasFocus = (ref?.current === document.activeElement)
      if(!hasFocus) {
          ref.current.focus()
      }
    }
}

const SectionHeading =  forwardRef(({ index, label, id, children, ...rest }, ref) => {  
  const h2ID = id || createLabelId(index)
  
  return  <Mui.Typography component="h2" tabIndex="-1" {...rest} id={h2ID} ref={ref}>
    {children || label}
  </Mui.Typography>
})

const enrichStepsWithStateDetail = ({
  activeStep,
  checkStateByKey
}) => {
  const filteredSteps = originalSteps.filter((originalStep, index) => {
    return originalStep?.index >= 0
  });
  const enrichedSteps = filteredSteps.map((originalStep, index) => {
    const active = originalStep?.index === activeStep;
    const tabIndex = active ? 0 : -1;
    const { isComplete: isStepComplete } = checkStateByKey
      ? checkStateByKey(originalStep?.key)
      : {};
    const labelId = createLabelId(originalStep?.index);
    let disabled = !(active || isStepComplete)
    if(disabled && originalStep?.index <= activeStep) {
      disabled = false
    }
    return {
      ...originalStep,
      active,
      tabIndex,
      isStepComplete,
      labelId,
      disabled
    };
  });
  return enrichedSteps
}

function DesktopStepper(props = {}) {
  const theme = Mui.useTheme();
  const { elevation, activeStep, checkStateByKey, setActiveStep, isRequestComplete, isRequestResolving, isReviewStep } = props;
  const { darkMode } = useContext(AppContext);
  const xsScreen = useMediaQuery(theme?.breakpoints.down(470));
  const disableHeaderLinks = isRequestComplete || isRequestResolving
  const _steps = enrichStepsWithStateDetail({
    activeStep, 
    checkStateByKey
  })
  const classes = useStyles()
  
  return (
    <Mui.Stepper
      elevation={darkMode ? elevation : 0}
      activeStep={(isRequestComplete && isReviewStep) ?  (activeStep + 1) : activeStep}
      orientation={xsScreen ? "vertical" : "horizontal"}
      alternativeLabel={xsScreen ? false : true}
      component="ol"
      className="margin-0"
      nonLinear
    >
      {_steps.map(({ isStepComplete, index, key, label, active, disabled: _disabledForStep }, idx) => {
        const disabled = disableHeaderLinks || _disabledForStep
        const onCurrentStep = activeStep === -1 ? (index <= 0) : (activeStep === index)
        const onLastStep = idx === (_steps.length - 1) 
        const completed = onLastStep ? isStepComplete : (isStepComplete && !onCurrentStep)
        return (
          <Mui.Step key={key} role="listitem">
            <Mui.StepButton 
              component="a"
              aria-current={onCurrentStep ? "step" : false}
              disabled={disabled}
              aria-disabled={disabled}
              role="link"
              onClick={() => {
                setActiveStep(currentActive => {
                  if(currentActive === -1  && !index) {
                    return currentActive
                  }
                  return index
                })
              }} 
              className={classes.stepIcon}
              completed={completed}
            >
                  <Mui.StepLabel StepIconProps={{ classes: {
                    root: (!active && !isStepComplete) && classes.outlineCircle,
                    text: (!active && !isStepComplete) && classes.outlineCircleText,
                    completed: classes.completed
                  }}}>
                  {completed && (
                    <Mui.Typography variant="srOnly">Completed: </Mui.Typography>
                  )}
                  {label}
              </Mui.StepLabel>

            </Mui.StepButton>
          </Mui.Step>
        );
      })}
    </Mui.Stepper>
  );
}

function MobileStepper(props) {
  const { activeStep, focusableId, isRequestComplete, isReviewStep, isRequestResolving, errorSubmitting } = props
  const currentStep = originalSteps.find(({ index }) => index === activeStep)
  const nextStep = originalSteps.find(({ index }) => index === (activeStep + 1))
  const classes = useStyles()
  const valueOfProgress = ((activeStep + 1)/numOfStepsToComplete) * 100
  const ref = useRef()
  const disableHeaderLinks = isRequestComplete || isRequestResolving

  useEffect(() => {
    focusOnRef(ref, focusableId)
  }, [focusableId, disableHeaderLinks])

  let secondaryText = "", 
    circularProgress = {
      value: valueOfProgress
    }
  if(nextStep?.label) {
    secondaryText = <Fragment>Next Step: <Mui.Typography variant="body2"  component="span" color="textPrimary">{nextStep?.label}</Mui.Typography></Fragment>
  } else if(errorSubmitting) {
    secondaryText = "Error"
  } else if (isReviewStep && !isRequestComplete && !isRequestResolving) {
    secondaryText = "Submit to complete"
  } else if (isRequestComplete) {
    secondaryText = "Completed"
    circularProgress.value = 100
    circularProgress.color = "success"
  }

  return <Mui.Box className={classes.mobileStepper} display="flex" justifyContent="space-between">
    <Mui.Box alignItems="flex-end">
      <SectionHeading {...currentStep}variant="h6" tabIndex="-1" ref={ref} />
      {secondaryText && <Mui.Typography id={htmlIdentifiers.nextLabel} variant="body2" color="secondary">{secondaryText}</Mui.Typography>}
    </Mui.Box>
    {
      (isReviewStep && (isRequestComplete || errorSubmitting)) 
        ? errorSubmitting ? <ErrorOutline className={classes.errorCircle} /> : <CheckCircleOutline className={classes.checkCircle}  />
        : <Mui.Box position="relative" display="inline-flex" className={classes.stepperProgress} >
          <Mui.CircularProgress size="4rem" variant="determinate" {...circularProgress} />
          <Mui.Box className={classes.progress}
          >
            <Mui.Typography
              variant="caption"
              component="div"
              color="textSecondary"
            >{activeStep + 1} of {numOfStepsToComplete}</Mui.Typography>
          </Mui.Box>
      </Mui.Box>
    }
  </Mui.Box>
}

function Header ({ stepRoRenderAsActive, activeStep, hasPageError, headingRef, ...rest }) {
  const props = {
    ...rest,
    activeStep
  }

  const activeStepInfo = originalSteps.find(({ index }) => index === stepRoRenderAsActive)
  const activeLabel = activeStepInfo?.label || ""
  const classes = useStyles()
  const onLoginScreen = activeStep === -1
  let stepHeadingText = "", stepOf = false, nextStep = false
  if(onLoginScreen) { 
    stepHeadingText = "Login Options"
  } else {
    const formattedStepHeading = createStepHeadingTitle({ label: activeLabel, step: stepRoRenderAsActive + 1 }) 
    if(formattedStepHeading?.stepOf) {
      stepOf = formattedStepHeading?.stepOf
    }
    if (formattedStepHeading?.label) {
      stepHeadingText = formattedStepHeading?.label
    }
    if(formattedStepHeading?.nextStep) {
      nextStep = formattedStepHeading?.nextStep
    }
  }
  if(hasPageError) {
    stepHeadingText = "Something went wrong"
  }
  if(!stepHeadingText) {
    stepHeadingText = activeLabel
  }
  const stepHeading = stepHeadingText ?? false
  const stepHeadingId = (activeStepInfo?.key || activeStep)
  const headingIsPreparedForFocusing = headingRef?.current?.id === stepHeadingId
  useEffect(() => {
    const childOfMainIsAlreadyFocused = isActiveElementDescendantOfMain(true)
    const okayToSetFocus = headingIsPreparedForFocusing 
      && !childOfMainIsAlreadyFocused
    const isOnContactOrLogin = [0, -1].includes(activeStep)
    if(okayToSetFocus && isOnContactOrLogin) {
      headingRef.current && headingRef.current.focus()
    }
  }, [activeStep, headingIsPreparedForFocusing, headingRef])
  const headingProps = {
    id: stepHeadingId,
    className: classes.stepHeading + " text-align-center",
    ref: headingRef,
    variant: "h4",
    component: "h2",
    label: stepHeading,
  }

  if(hasPageError) {
    return <SectionHeading {...headingProps} />
  }

  return <Fragment>
    <Mui.Hidden mdUp>
      <MobileStepper {...props} />
    </Mui.Hidden>
    <Mui.Hidden smDown>
      <Mui.Box component="nav" className={classes.navWrapper} aria-label="Form Steps" aria-describedby={stepHeadingId || undefined}>
        <DesktopStepper {...props} />
      </Mui.Box>
          <SectionHeading {...headingProps} />
        <Subtitle stepOf={stepOf} nextStep={nextStep} />
    </Mui.Hidden>
  </Fragment>
}

const Subtitle = forwardRef( ({ stepOf, nextStep }, ref) => {
  if(!stepOf && !nextStep) {
    return false
  }
  
  return <Mui.Typography ref={ref} variant="caption" color="secondary" className="text-align-center">
    {stepOf}
    {(stepOf && nextStep) && " | "}
    <span id={htmlIdentifiers.nextLabel}>
      {nextStep}
    </span>
  </Mui.Typography>
})

export default Header;
